import {
    useCallback,
    useEffect,
    useId,
    useRef,
} from 'react';
import { toast } from 'sonner';
import { ApiChatMessageKind, ApiChatReadState, type ChatDialogResponse } from '@/core/generated/api';
import { useAuth } from '@/entities/Auth/store';
import { useChatDialogStore } from '@/entities/ChatDialog/model/store';
import type { ChatDialogRealtimeCallbacks } from '@/entities/ChatDialog/model/types';
import { RequestStatus } from '@/shared/model/requestStatus';
import { ChatIncomingToast } from './ChatIncomingToast';
import { CHAT_INCOMING_TOASTER_ID } from '../model/config';

/** Уведомляет о новых входящих сообщениях без повторов при обновлении переписки */
export const useChatDialogNotifications = (
    enabled: boolean,
    onOpen: (dialog: ChatDialogResponse) => void,
    store: typeof useChatDialogStore = useChatDialogStore,
): ChatDialogRealtimeCallbacks['onEvent'] => {
    const sessionRevision = useAuth((state) => state.sessionRevision);
    const latest = useRef(new Map<string, { sequence: number; revision: bigint }>());
    const instance = useId();
    const active = useRef(new Map<string, bigint>());

    useEffect(() => {
        const messages = latest.current;
        const notifications = active.current;

        return () => {
            messages.clear();
            notifications.forEach((_, id) => toast.dismiss(id));
            notifications.clear();
        };
    }, [enabled, sessionRevision, store]);

    return useCallback((event) => {
        const state = store.getState();
        const dialog = event.dialog;
        const id = dialog?.id;

        if (!enabled || !state.realtimeEnabled || state.refreshStatus !== RequestStatus.Success || !id) return;
        if ((event.message?.editVersion ?? 0) > 0) return;

        const previous = latest.current.get(id);
        const revision = BigInt(event.revision);

        if (previous && revision < previous.revision) return;
        if (!event.message) return;

        const existing = state.selectedDialog?.id === id
            ? state.selectedDialog
            : state.dialogs.find((item) => item.id === id);

        const knownSequence = Math.max(previous?.sequence ?? 0, existing?.lastMessage?.sequence ?? 0);
        const message = dialog.lastMessage;
        const sequence = message?.sequence ?? 0;

        latest.current.set(id, {
            sequence: Math.max(knownSequence, event.message.isOutgoing
                ? event.message.sequence ?? 0
                : sequence),
            revision,
        });

        const newIncoming = sequence > knownSequence && message?.isOutgoing === false
            && message.kind?.id === ApiChatMessageKind.Regular && !event.message.isOutgoing
            && message.readState?.id === ApiChatReadState.Unread;

        if (!newIncoming) return;
        const notificationId = `chat-incoming:${instance}:${id}`;
        const notifications = active.current;
        notifications.set(notificationId, revision);

        /** Удаляет только актуальную версию уведомления */
        const forget = (): void => {
            if (notifications.get(notificationId) === revision) notifications.delete(notificationId);
        };

        toast.custom(() => (
            <ChatIncomingToast
                dialog={dialog}
                onOpen={() => {
                    toast.dismiss(notificationId);
                    notifications.delete(notificationId);
                    onOpen(dialog);
                }}
                onClose={() => {
                    toast.dismiss(notificationId);
                    notifications.delete(notificationId);
                }}
            />
        ), {
            id: notificationId,
            toasterId: CHAT_INCOMING_TOASTER_ID,
            position: 'bottom-left',
            duration: 8000,
            onAutoClose: forget,
            onDismiss: forget,
        });
    }, [enabled, onOpen, store, instance]);
};
